import { getGianoConnector } from "@/config/wagmi"
import { DEFAULT_CREDENTIAL_NAME } from "@/lib/giano"
import { AuthenticationJSON, ICompleteChallengeResponse, PasskeyResponseType, RegistrationJSON } from "@portafino/shared"
import { useCallback, useMemo } from "react"
import { ApiError } from "@/utils/api"

export interface ChallengeProps {
    challenge?: string
    error?: ApiError
    isLoading?: boolean
    refetch?: () => void
}

interface CompleteChallengeParams {
    credential: RegistrationJSON | AuthenticationJSON
    challenge: string
    type: PasskeyResponseType
}

export interface CompleteChallengeProps {
    completeChallenge: (params: CompleteChallengeParams) => Promise<ICompleteChallengeResponse>
    error?: ApiError
    isPending: boolean
    onSuccess?: (data: ICompleteChallengeResponse) => void
}

interface Props {
    action: 'signup' | 'signin'
    credentialName?: string
    challenge: ChallengeProps
    completeChallenge: CompleteChallengeProps
}

export const useGianoConnector = ({ action, credentialName = DEFAULT_CREDENTIAL_NAME, challenge: challengeProps, completeChallenge: completeChallengeProps }: Props) => {
    const { challenge, error: errorFetchingChallenge, isLoading: isLoadingChallenge, refetch: refetchChallenge } = challengeProps
    const { completeChallenge, error: errorCompletingChallenge, isPending: isPendingCompletingChallenge, onSuccess: onCompleteChallengeSuccess } = completeChallengeProps
    
    const onCompleteRegistrationChallenge = useCallback(async (credential: RegistrationJSON, challenge: string) => {
        const data = await completeChallenge({ challenge, credential, type: PasskeyResponseType.REGISTRATION })
        onCompleteChallengeSuccess?.(data)
        return { address: data.address || '' }
    }, [completeChallenge, onCompleteChallengeSuccess])

    const onCompleteSigninChallenge = useCallback(async (credential: AuthenticationJSON, challenge: string) => {
        const data = await completeChallenge({ challenge, credential, type: PasskeyResponseType.AUTHENTICATION })
        onCompleteChallengeSuccess?.(data)
        return { publicKey: data.publicKey || '' }
    }, [completeChallenge, onCompleteChallengeSuccess])

    const gianoConnector = useMemo(() => {
        return getGianoConnector({ challenge, action, credentialName, onCompleteRegistrationChallenge, onCompleteSigninChallenge })
    }, [challenge, action, credentialName, onCompleteRegistrationChallenge, onCompleteSigninChallenge])

    return {
        challenge,
        gianoConnector, 
        errorFetchingChallenge, 
        isLoadingChallenge, 
        refetchChallenge, 
        errorCompletingChallenge, 
        isPendingCompletingChallenge
    }
}
